'use client';

import Icon from '@/components/ui/AppIcon';

interface CartSummaryProps {
  itemCount: number;
  totalAmount: number;
  estimatedProcessingDays: number;
  onProceedToForm: () => void;
  onClearCart: () => void;
  onUpdateQuantities: () => void;
  wholesalePricing?: boolean;
}

const CartSummary = ({
  itemCount,
  totalAmount,
  estimatedProcessingDays,
  onProceedToForm,
  onClearCart,
  onUpdateQuantities,
  wholesalePricing = false,
}: CartSummaryProps) => {
  return (
    <div className="sticky top-20 rounded-lg bg-card p-6 shadow-md">
      <h2 className="mb-4 font-heading text-xl font-semibold text-text-primary">Итоги заказа</h2>

      {wholesalePricing && (
        <div className="mb-4 rounded-md bg-muted px-3 py-2 font-caption text-xs text-text-primary">
          Итог по оптовым ценам (промокод). В заявке уйдёт тот же код.
        </div>
      )}

      <div className="mb-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="font-caption text-sm text-text-secondary">Товаров в заказе:</span>
          <span className="font-heading text-base font-medium text-text-primary">
            {itemCount} {itemCount === 1 ? 'товар' : itemCount < 5 ? 'товара' : 'товаров'}
          </span>
        </div>

        <div className="flex items-center justify-between border-t border-border pt-3">
          <span className="font-heading text-base font-medium text-text-primary">Общая сумма:</span>
          <span className="font-heading text-2xl font-bold text-accent">
            {totalAmount.toLocaleString('ru-RU')} ₸
          </span>
        </div>

        <div className="flex items-start gap-2 rounded-md bg-muted p-3">
          <Icon name="ClockIcon" size={20} className="mt-0.5 flex-shrink-0 text-text-secondary" />
          <div>
            <p className="font-caption text-xs font-medium text-text-primary">
              Ориентировочное время обработки
            </p>
            <p className="font-caption text-xs text-text-secondary">
              {estimatedProcessingDays}{' '}
              {estimatedProcessingDays === 1
                ? 'рабочий день'
                : estimatedProcessingDays < 5
                  ? 'рабочих дня'
                  : 'рабочих дней'}
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <button
          onClick={onProceedToForm}
          className="flex w-full items-center justify-center gap-2 rounded-md bg-accent px-6 py-3 font-heading text-base font-semibold text-accent-foreground shadow-sm transition-all duration-250 hover:bg-accent/90 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 active:scale-[0.97]"
        >
          <span>Оформить заявку</span>
          <Icon name="ArrowRightIcon" size={20} />
        </button>

        <button
          onClick={onUpdateQuantities}
          className="flex w-full items-center justify-center gap-2 rounded-md border border-border bg-background px-6 py-2.5 font-heading text-sm font-medium text-text-primary transition-all duration-250 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 active:scale-[0.97]"
        >
          <Icon name="ArrowPathIcon" size={18} />
          <span>Обновить количество</span>
        </button>

        <button
          onClick={onClearCart}
          className="flex w-full items-center justify-center gap-2 rounded-md border border-error bg-background px-6 py-2.5 font-heading text-sm font-medium text-error transition-all duration-250 hover:bg-error/10 focus:outline-none focus:ring-2 focus:ring-error focus:ring-offset-2 active:scale-[0.97]"
        >
          <Icon name="TrashIcon" size={18} />
          <span>Очистить корзину</span>
        </button>
      </div>

      <div className="mt-4 rounded-md bg-muted p-3">
        <div className="mb-2 flex items-center gap-2">
          <Icon name="InformationCircleIcon" size={18} className="text-text-secondary" />
          <span className="font-caption text-xs font-medium text-text-primary">
            Важная информация
          </span>
        </div>
        <ul className="space-y-1 font-caption text-xs text-text-secondary">
          <li>• Цены указаны в тенге (₸)</li>
          <li>• Минимальные количества обязательны</li>
          <li>• Заявка будет обработана менеджером</li>
          <li>• Окончательная цена подтверждается при обработке</li>
        </ul>
      </div>
    </div>
  );
};

export default CartSummary;
